import { HttpClient, HttpParams } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { Constants } from '@app/common/constants';
import { HttpHelper } from '@app/common/http-helper';
import { CacheService } from '@app/services/cache.service';
import { Event } from '@microsoft/microsoft-graph-types';
import { concat, Observable, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';

@Injectable({
  providedIn: 'root'
})
export class EventsService {

  constructor(private http: HttpClient, private cache: CacheService, private httpHelper: HttpHelper) { }

  private readonly cacheKeyPrefix = 'EventsService';

  public useCache = true;

  getMyEvents(numItems: number, startDateInUTC: Date, endDateInUTC: Date): Observable<Array<Event>> {

    const cacheKey = `${this.cacheKeyPrefix}-${'getMyEvents'}-${numItems.toString()}`;

    const httpParamsMyEvents = new HttpParams()
      .append('startDateTime', startDateInUTC.toISOString())
      .append('endDateTime', endDateInUTC.toISOString())
      .append('$orderBy', 'start/dateTime')
      .append('$top', numItems.toString());

    // Graph API call to fetch user events
    const fetchOperation = this.http.get<any>(`${Constants.GRAPH_API_ENDPOINT + Constants.GRAPH_API_VERSION_ONE}/me/calendar/calendarView`,
      {
        params: httpParamsMyEvents
      })
      .pipe(
        map(result => {
          return result.value as Array<Event>;
        }),
        tap(data => {
          this.useCache && this.cache.set(cacheKey, data);
        }),
        catchError(this.httpHelper.handleError({ operation: 'getMyEvents', result: new Array<Event>(), cacheKey: cacheKey })));

    if (this.useCache) {
      const cachedData = this.cache.get<Array<Event>>(cacheKey);
      if (cachedData) {
        return concat(of(cachedData), fetchOperation);
      }
    }
    return fetchOperation;
  }

  acceptEvent(id: string): Observable<Event> {
    return this.http.post<any>(`${Constants.GRAPH_API_ENDPOINT + Constants.GRAPH_API_VERSION_ONE}/me/calendar/events/${id}/accept`, { observe: 'response' })
      .pipe(
        tap(response => {
          console.log(`accepted event: `);
          console.log(response);
        }),
        catchError(this.httpHelper.handleError({ operation: 'acceptEvent', result: {}, rethrow: true })));
  }

  tentativelyAcceptEvent(id: string): Observable<Event> {
    return this.http.post<any>(`${Constants.GRAPH_API_ENDPOINT + Constants.GRAPH_API_VERSION_ONE}/me/calendar/events/${id}/tentativelyAccept`, {})
      .pipe(
        tap(response => {
          console.log(`tentatively accepted event: `);
          console.log(response);
        }),
        catchError(this.httpHelper.handleError({ operation: 'tentativelyAcceptEvent', result: {}, rethrow: true })));
  }

  declineEvent(id: string): Observable<Event> {
    return this.http.post<any>(`${Constants.GRAPH_API_ENDPOINT + Constants.GRAPH_API_VERSION_ONE}/me/calendar/events/${id}/decline`, {})
      .pipe(
        tap(response => {
          console.log(`declined event: `);
          console.log(response);
        }),
        catchError(this.httpHelper.handleError({ operation: 'declineEvent', result: {}, rethrow: true })));
  }

  cancelEvent(id: string): Observable<Event> {
    return this.http.post<any>(`${Constants.GRAPH_API_ENDPOINT + Constants.GRAPH_API_BETA_VERSION}/me/events/${id}/cancel`, {})
      .pipe(
        tap(response => {
          console.log(`canceled event: `);
          console.log(response);
        }),
        catchError(this.httpHelper.handleError({ operation: 'cancelEvent', result: {}, rethrow: true })));
  }

  deleteEvent(id: string): Observable<Event> {
    return this.http.delete<any>(`${Constants.GRAPH_API_ENDPOINT + Constants.GRAPH_API_VERSION_ONE}/me/calendar/events/${id}`, {})
      .pipe(
        tap(response => {
          console.log(`deleted event: `);
          console.log(response);
        }),
        catchError(this.httpHelper.handleError({ operation: 'deleteEvent', result: {}, rethrow: true })));
  }

  getEvent(id: string): Observable<Event> {
    return this.http.get<any>(`${Constants.GRAPH_API_ENDPOINT + Constants.GRAPH_API_VERSION_ONE}/me/events/${id}`)
      .pipe(
        map(result => {
          return result as Event;
        }),
        catchError(this.httpHelper.handleError({ operation: 'getEvent', result: {} })));
  }
}
